import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
    try {
        const date = new Date(d);
        return date.toLocaleDateString("en-AU", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

export default function InsightsPreview() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/posts?limit=3`)
            .then((r) => {
                if (alive) setPosts(r.data || []);
            })
            .catch(() => alive && setPosts([]))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, []);

    return (
        <section
            id="insights"
            data-testid="insights-section"
            className="py-24 md:py-32 lg:py-40"
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-14 md:mb-20 items-end">
                    <div className="col-span-12 md:col-span-7">
                        <span className="number-mark text-ink2">§ 08</span>
                        <h2
                            data-testid="insights-heading"
                            className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink"
                        >
                            Insights
                            <br />
                            <em className="italic font-light text-eucalyptus">
                                from the work.
                            </em>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 flex md:justify-end">
                        <Link
                            to="/insights"
                            data-testid="insights-all-link"
                            className="inline-flex items-center px-6 py-3 border border-ink text-ink text-[11px] tracking-[0.22em] uppercase hover:bg-ink hover:text-bone transition-colors"
                        >
                            All insights →
                        </Link>
                    </div>
                </div>

                <div
                    data-testid="insights-grid"
                    className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-rule"
                >
                    {loading &&
                        [0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="border-r border-b border-rule p-8 md:p-10 min-h-[280px] animate-pulse"
                            >
                                <div className="h-3 w-24 bg-rule mb-6" />
                                <div className="h-6 w-3/4 bg-rule mb-3" />
                                <div className="h-4 w-full bg-rule mb-2" />
                                <div className="h-4 w-5/6 bg-rule" />
                            </div>
                        ))}
                    {!loading &&
                        posts.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/insights/${p.slug}`}
                                data-testid={`insight-card-${p.slug}`}
                                className="border-r border-b border-rule group bg-bone hover:bg-bone2 transition-colors flex flex-col"
                            >
                                {p.image && (
                                    <div className="aspect-[16/10] overflow-hidden border-b border-rule">
                                        <img
                                            src={p.image}
                                            alt=""
                                            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]"
                                            loading="lazy"
                                        />
                                    </div>
                                )}
                                <div className="p-8 md:p-9 flex-1 flex flex-col">
                                    <div className="flex items-center justify-between mb-5">
                                        <span className="number-mark text-ochre">
                                            {p.category || "Note"}
                                        </span>
                                        <span className="text-[11px] text-ink2 tracking-wide">
                                            {formatDate(p.published_at)}
                                        </span>
                                    </div>
                                    <h3 className="font-serif text-2xl leading-[1.15] text-ink group-hover:text-ochre transition-colors">
                                        {p.title}
                                    </h3>
                                    <p className="mt-4 text-ink2 text-[15px] leading-relaxed flex-1">
                                        {p.excerpt}
                                    </p>
                                    <span className="mt-6 eyebrow text-ink2 group-hover:text-ochre transition-colors">
                                        Read →
                                    </span>
                                </div>
                            </Link>
                        ))}
                    {!loading && posts.length === 0 && (
                        <div className="border-r border-b border-rule p-10 col-span-full text-ink2">
                            Insights coming soon.
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
